import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Microscope, Heart, Leaf, BookMarked } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const PluriMedPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-900 to-blue-700 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/19qqt0ph_SAS%20Health%20Sciences.JPG)',
            backgroundPosition: 'center 35%'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              PluriMed
            </h1>
            <p className="text-2xl md:text-3xl mb-4 text-purple-100">
              Medical Pluralism in Highland Asia
            </p>
            <p className="text-xl text-purple-50 leading-relaxed">
              Advancing critical inquiry into therapeutic ecologies and health knowledge systems
            </p>
          </div>
        </div>
      </section>

      {/* Conference Report */}
      <section className="py-16 bg-purple-50 border-b-4 border-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                <span className="text-sm font-bold text-purple-600 uppercase tracking-wider">Conference Report</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Entangled Medical Futures in Highland Asia
              </h2>
              <h3 className="text-xl font-semibold text-purple-700 mb-6">
                8–10 October 2025, Edinburgh
              </h3>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📅</span>
                  <div>
                    <p className="font-semibold text-gray-900">Conference Dates</p>
                    <p className="text-gray-700">8–10 October 2025</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📍</span>
                  <div>
                    <p className="font-semibold text-gray-900">Location</p>
                    <p className="text-gray-700">University of Edinburgh, Centre for South Asian Studies & Edinburgh Centre for Medical Anthropology</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-2xl">🤝</span>
                  <div>
                    <p className="font-semibold text-gray-900">Organizers</p>
                    <p className="text-gray-700">Highland Institute in partnership with the British Academy</p>
                  </div>
                </div>
              </div>

              <div className="prose prose-lg max-w-none mb-8">
                <p className="text-gray-700 leading-relaxed mb-4">
                  The Highland Institute, in partnership with the British Academy, co-organised and co-sponsored the international 
                  conference <strong>"Entangled Medical Futures: Plural Medicine, Climate-Altered Worlds, and More-Than-Human Care 
                  in Highland Asia"</strong>, held from 8 to 10 October 2025 at the University of Edinburgh. Hosted by the Centre for 
                  South Asian Studies and the Edinburgh Centre for Medical Anthropology, the event brought together leading researchers, 
                  clinicians, and students from across Bhutan, Nepal, India, Europe, and North America.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  Over three days of presentations and discussions, participants explored how healing is practiced and understood within 
                  the culturally diverse and ecologically dynamic highlands of Asia. The conference examined themes central to the PluriMed 
                  network: the coexistence of multiple medical systems, the impact of environmental change on health and healing, and the 
                  complex relations among humans, spirits, landscapes, and institutions.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-4">
                  Speakers highlighted how Indigenous healers, community health workers, biomedical practitioners, and ritual specialists 
                  navigate fractured infrastructures, shifting ecologies, and diverse forms of therapeutic authority. Discussions ranged from 
                  Bon-Buddhist understandings of illness in Bhutan and Amchi practice in Dolpo to plural mental health strategies in South 
                  Asia, solastalgia among Nepali youth, and changing Sowa Rigpa practices in Kathmandu. Additional sessions focused on 
                  antimicrobial resistance, cross-border health-seeking, and the lived realities of medical pluralism across highland communities.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-6">
                  The conference fostered a collaborative space for comparative and interdisciplinary exchange and marked an important step 
                  in building the PluriMed Network.
                </p>

                <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded mb-6">
                  <p className="font-semibold text-purple-900 mb-3">Outcomes & Publications</p>
                  <p className="text-gray-700 mb-2">Two major outcomes are now underway:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>A special journal issue featuring selected conference papers</li>
                    <li>An edited volume designed for graduate and postgraduate students, synthesising key debates on plural medicine, 
                    ecology, and care in Highland Asia</li>
                  </ul>
                </div>

                <p className="text-gray-700 leading-relaxed italic">
                  The Highland Institute is grateful to all partners and participants who contributed to this rich and wide-ranging event.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <p className="font-semibold text-gray-900 mb-3">For more information:</p>
                <div className="space-y-2">
                  <p className="text-gray-700 font-medium">Michael T. Heneise</p>
                  <p className="text-gray-600 text-sm">British Academy Visiting Fellow, University of Edinburgh</p>
                  <p className="flex items-center gap-2 text-gray-700 mt-2">
                    <span>📧</span>
                    <a href="mailto:v1mhenei@ed.ac.uk" className="text-purple-600 hover:underline">
                      v1mhenei@ed.ac.uk
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">About PluriMed</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The <strong>PluriMed Research Group</strong> is an interdisciplinary collective based at the Highland 
                Institute, dedicated to advancing critical inquiry into medical pluralism, therapeutic ecologies, 
                and the intersections of formal and informal health knowledge systems.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Anchored in rigorous ethnographic research and grounded in the lived realities of communities across 
                Highland Asia, the group serves as both a research hub and an intellectual companion to <em>PluriMed</em>, 
                the Highland Institute's open access journal published through Highlander Press.
              </p>
              <p className="text-gray-700 leading-relaxed">
                PluriMed operates at the convergence of medical anthropology, global health, environmental humanities, 
                and speculative biomedicine. The group's work is shaped by a commitment to epistemic plurality and 
                postcolonial critique.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Current Research Areas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Microscope className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">Fungal Disease Burden</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  Neglected infections in Northeast India and Himalayan borderlands
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Heart className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">Indigenous Healing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  Local healing systems in relation to biomedical and public health frameworks
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Leaf className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">Environmental Health</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  Links between environmental change and emergent pathologies
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <BookMarked className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">Medical Uncertainty</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  Ethnographic approaches to therapeutic decision-making
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Our Approach</h2>
            <div className="bg-purple-50 p-8 rounded-lg">
              <p className="text-gray-700 mb-6">
                The PluriMed Research Group engages scholars, practitioners, and independent researchers who seek 
                to rethink prevailing assumptions about disease, diagnosis, healing, and evidence.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Epistemic Plurality:</strong> Valuing multiple knowledge systems and ways of knowing
                  </span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Postcolonial Critique:</strong> Challenging Western-centric medical paradigms
                  </span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Field-Based Research:</strong> Grounded in lived realities of highland communities
                  </span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Collaborative Scholarship:</strong> Workshops, colloquia, and joint writing projects
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Global Collaborations</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              The PluriMed Research Group collaborates with institutional and community partners across multiple regions:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-800 font-medium">South & Southeast Asia</p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-800 font-medium">United Kingdom</p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-800 font-medium">United States</p>
              </div>
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-800 font-medium">Norway</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">PluriMed Journal</h2>
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl">Open Access Publications</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">
                  <em>PluriMed</em> is the Highland Institute's open access journal published through Highlander Press, 
                  featuring rigorous scholarship on medical pluralism, therapeutic ecologies, and health knowledge systems.
                </p>
                <a
                  href="https://journals.highlanderpress.org/index.php/Plurimed"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
                >
                  Visit Journal
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Join the Conversation</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            For inquiries, collaborations, or to propose contributions to PluriMed journal
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="mailto:plurimed@highlandinstitute.org">
              <Button className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact PluriMed
              </Button>
            </a>
            <Link to="/research">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Explore Research
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PluriMedPage;